// src/auditoria/components/ds/DsPanelCard.tsx
//
// Implementa a `.card` de painel/gráfico do canvas (DESIGN-SYSTEM.md §5.1):
// `<div class="card" style="padding:20px 24px">` + título + sublinha, na forma
// longa `h2` 16px/700 + `p` 12px de `dte-dashboard` e `acao-fiscal`.
// Instancia em **43** artboards.
//
// É o container obrigatório de `DsBarList`, `DsGantt`, `DsDonut` e de qualquer
// `<Chart>` sobrevivente (§7.3). Não usa `CardHeader`/`CardContent`: o tema
// entrega `h6` 17px/600 + `body2` 14px com padding 24px
// (`src/theme/core/components/card.tsx:24-33`), dois pontos acima do canvas.
// Sem `Divider` interno.

import type { Theme, SxProps } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';

// ----------------------------------------------------------------------

export type DsPanelCardProps = {
  title: string;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  /** Padding 16px em vez de 20px 24px 16px. */
  dense?: boolean;
  sx?: SxProps<Theme>;
};

// ----------------------------------------------------------------------

export function DsPanelCard({
  title,
  subtitle,
  action,
  children,
  dense = false,
  sx,
}: DsPanelCardProps) {
  return (
    <Card sx={[{ padding: dense ? '16px' : '20px 24px 16px' }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box component="h2" sx={{ fontSize: 16, fontWeight: 700, m: 0 }}>
            {title}
          </Box>

          {subtitle && (
            <Box component="p" sx={{ fontSize: 12, color: 'text.secondary', m: '4px 0 12px' }}>
              {subtitle}
            </Box>
          )}
        </Box>

        {action && <Box sx={{ flex: '0 0 auto' }}>{action}</Box>}
      </Box>

      {children}
    </Card>
  );
}
